import { HRM3050_R01DetailDto, HRM3050_R01HeaderDto } from "./Dto_HRM3050_R01";

/** 화면의 두 그리드를 구분하는 값이다. header는 위쪽 근무조, detail은 아래쪽 휴게시간 그리드이다. */
export type HRM3050_R01GridType = 'header' | 'detail';

/** 하루의 분(24 × 60)이다. 자정을 넘기는 시각을 다음날로 옮길 때 더한다. */
const MINUTES_OF_DAY = 1440;

/** 24시간제 HH:MM(00:00~23:59)만 통과시키는 정규식이다. 백엔드 HRM3050_R01Service.TIME_PATTERN과 같다. */
const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

/** 근무조 그리드 컬럼의 필드명과 사용자 표시명을 연결한다. */
export const headerNames_HRM3050_R01: Record<string, string> = {
    rowIndex: '',
    shiftCd: '근무조',
    shiftNm: '근무조명',
    startTime: '정시 출근',
    endTime: '정시 퇴근',
    useYn: '사용',
    remark: '비고',
};

/** 휴게시간 그리드 컬럼의 필드명과 사용자 표시명을 연결한다. breakMinutes·breakHow는 화면 계산 컬럼이다. */
export const detailHeaderNames_HRM3050_R01: Record<string, string> = {
    rowIndex: '',
    breakSeq: '순번',
    breakNm: '휴게명',
    fromTime: '시작',
    toTime: '종료',
    breakMinutes: '시간(분)',
    excludeYn: '근무제외',
    breakHow: '처리',
    useYn: '사용',
    remark: '비고',
};

/**
 * 그리드에 입력한 시각을 HH:MM으로 정리한다. 실행 샘플과 같이 2130 → 21:30, 9:00 → 09:00을 허용한다.
 * @param value 입력값
 * @returns 정리한 HH:MM 문자열. 숫자 형태로 바꿀 수 없으면 앞뒤 공백만 지운 원래 값을 반환해 저장 검증에서 걸러지게 한다.
 */
export const formatHRM3050_R01Time = (value: unknown): string => {
    // ??: 값이 null·undefined이면 빈 문자열로 다룬다.
    const text = String(value ?? '').trim();
    // 정규식 /^(\d{1,2}):?(\d{2})$/: 콜론이 있든 없든 시 1~2자리 + 분 2자리를 찾는다.
    const matched = text.match(/^(\d{1,2}):?(\d{2})$/);
    if (!matched) return text;
    // 구조 분해: 정규식 결과에서 시·분 부분만 꺼낸다.
    const [, hour, minute] = matched;
    return `${hour.padStart(2, '0')}:${minute}`;
};

/**
 * 값이 HH:MM(00:00~23:59) 형식인지 검사한다.
 * @param time 검사할 시각 문자열
 * @returns 형식이 맞으면 true
 */
export const validateHRM3050_R01Time = (time: string | null | undefined): boolean =>
    !!time && TIME_PATTERN.test(time);

/**
 * HH:MM을 0시 기준 분으로 바꾼다.
 * @param time 형식 검사를 통과한 HH:MM 문자열
 * @returns 0~1439 사이의 분
 */
const getHRM3050_R01Minutes = (time: string): number =>
    Number(time.slice(0, 2)) * 60 + Number(time.slice(3, 5));

/**
 * 시작·종료 시각을 분 구간으로 바꾼다. 종료가 시작보다 작거나 같으면 다음날로 보고 1440분을 더한다.
 * @param fromTime 시작 HH:MM
 * @param toTime 종료 HH:MM
 * @returns [시작분, 종료분]
 */
const getHRM3050_R01Interval = (fromTime: string, toTime: string): [number, number] => {
    const fromMinute = getHRM3050_R01Minutes(fromTime);
    const toMinute = getHRM3050_R01Minutes(toTime);
    // 삼항 연산자: 종료가 시작보다 앞이면 자정을 넘긴 구간이므로 하루를 더한다.
    return [fromMinute, toMinute <= fromMinute ? toMinute + MINUTES_OF_DAY : toMinute];
};

/**
 * 두 구간이 겹치는 분을 계산한다.
 * @returns 겹치는 분. 겹치지 않으면 0
 */
const getHRM3050_R01OverlapMinutes = (fromA: number, toA: number, fromB: number, toB: number): number =>
    Math.max(0, Math.min(toA, toB) - Math.max(fromA, fromB));

/**
 * 휴게 한 행의 시간(분)을 계산한다. 그리드의 시간(분) 컬럼과 합계 행에 쓴다.
 * @param row 휴게 행
 * @returns 휴게 분. 시각 형식이 맞지 않으면 0
 */
export const getHRM3050_R01BreakMinutes = (row: Pick<HRM3050_R01DetailDto, 'fromTime' | 'toTime'>): number => {
    // 입력 중이라 형식이 맞지 않으면 0분으로 표시한다.
    if (!validateHRM3050_R01Time(row.fromTime) || !validateHRM3050_R01Time(row.toTime)) return 0;
    const [fromMinute, toMinute] = getHRM3050_R01Interval(row.fromTime, row.toTime);
    return toMinute - fromMinute;
};

/** 선택 근무조의 정시 기준 시간 요약이다. 단위는 모두 분이다. */
export interface HRM3050_R01ShiftSummary {
    stayMinutes: number; // 정시 체류 = 퇴근 − 출근 (자정 넘김 보정)
    excludedBreakMinutes: number; // 정시 구간과 겹치는 근무제외(Y) 휴게 합계
    workMinutes: number; // 1일 소정근로 = 정시 체류 − 근무제외 휴게
    allBreakMinutes: number; // 정시 구간과 겹치는 모든 사용 휴게 합계. 지각·조퇴·외출에서 빼는 휴게
}

/**
 * 선택 근무조와 휴게 목록으로 정시 기준 시간 요약을 계산한다. 실행 샘플의 brk·shiftNet 계산과 같다.
 * 휴게는 같은 날 구간과 하루 뒤로 옮긴 구간을 모두 정시 구간과 비교한다. 야간조의 다음날 01:00 휴게를 잡기 위해서이다.
 * @param header 근무조 행
 * @param breakList 삭제 표시를 뺀 휴게 행 목록
 * @returns 요약 값. 정시 출퇴근 형식이 맞지 않으면 null
 */
export const getHRM3050_R01ShiftSummary = (
    header: Pick<HRM3050_R01HeaderDto, 'startTime' | 'endTime'>,
    breakList: HRM3050_R01DetailDto[],
): HRM3050_R01ShiftSummary | null => {
    // 출퇴근 시각이 아직 올바르지 않으면 계산할 수 없다.
    if (!validateHRM3050_R01Time(header.startTime) || !validateHRM3050_R01Time(header.endTime)) return null;
    const [startMinute, endMinute] = getHRM3050_R01Interval(header.startTime, header.endTime);
    /** 조건에 맞는 사용 휴게가 정시 구간과 겹치는 분의 합계를 반환한다. */
    const sumBreakMinutes = (isExcludedOnly: boolean) => breakList
        .filter(row => row.useYn === 'Y'
            && validateHRM3050_R01Time(row.fromTime) && validateHRM3050_R01Time(row.toTime)
            && (!isExcludedOnly || row.excludeYn === 'Y'))
        .reduce((sum, row) => {
            const [fromMinute, toMinute] = getHRM3050_R01Interval(row.fromTime, row.toTime);
            return sum
                + getHRM3050_R01OverlapMinutes(startMinute, endMinute, fromMinute, toMinute)
                + getHRM3050_R01OverlapMinutes(startMinute, endMinute, fromMinute + MINUTES_OF_DAY, toMinute + MINUTES_OF_DAY);
        }, 0);
    const stayMinutes = endMinute - startMinute;
    const excludedBreakMinutes = sumBreakMinutes(true);
    return {
        stayMinutes,
        excludedBreakMinutes,
        workMinutes: stayMinutes - excludedBreakMinutes,
        allBreakMinutes: sumBreakMinutes(false),
    };
};

/**
 * 사용 중(Y)인 휴게끼리 시간이 겹치는 첫 쌍을 찾는다.
 * 실행 샘플은 같은 날 안에서만 비교해 자정을 넘는 휴게(23:30~00:30과 00:10~00:20)의 겹침을 놓치므로,
 * 백엔드와 같이 두 번째 구간을 하루 앞뒤로 옮겨서도 비교한다.
 * @param breakList 검사할 휴게 행 목록 (형식 검사를 통과한 행)
 * @returns 겹치는 두 행. 없으면 undefined
 */
export const findHRM3050_R01BreakOverlap = (
    breakList: HRM3050_R01DetailDto[],
): [HRM3050_R01DetailDto, HRM3050_R01DetailDto] | undefined => {
    const usedList = breakList.filter(row => row.useYn === 'Y');
    for (let i = 0; i < usedList.length; i++) {
        const [fromA, toA] = getHRM3050_R01Interval(usedList[i].fromTime, usedList[i].toTime);
        for (let j = i + 1; j < usedList.length; j++) {
            const [fromB, toB] = getHRM3050_R01Interval(usedList[j].fromTime, usedList[j].toTime);
            // 1분이라도 겹치면 그 쌍을 반환한다. 끝과 시작이 맞닿기만 하면 겹치지 않은 것으로 본다.
            const isOverlapped = [-MINUTES_OF_DAY, 0, MINUTES_OF_DAY]
                .some(offset => getHRM3050_R01OverlapMinutes(fromA, toA, fromB + offset, toB + offset) > 0);
            if (isOverlapped) return [usedList[i], usedList[j]];
        }
    }
    return undefined;
};

/**
 * 분을 시간 표시(소수 둘째 자리)로 바꾼다. 실행 샘플의 "8.00h" 표시와 같다.
 * @param minutes 분
 * @returns "8.00h" 형식 문자열
 */
export const formatHRM3050_R01Hours = (minutes: number): string => `${(minutes / 60).toFixed(2)}h`;
